import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ProgressSteps from '../components/ToolBox/ProgressSteps';
import Step1_SelectClinic from '../components/ToolBox/DoctorScheduleEditor/Step1_SelectClinic';
import Step2_AddSchedule from '../components/ToolBox/DoctorScheduleEditor/Step2_AddSchedule';
import Step3_SelectDoctors from '../components/ToolBox/DoctorScheduleEditor/Step3_SelectDoctors';
import Step4_Preview from '../components/ToolBox/DoctorScheduleEditor/Step4_Preview';
import Step5_Confirm from '../components/ToolBox/DoctorScheduleEditor/Step5_Confirm';

const DoctorScheduleEditor = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [clinicId, setClinicId] = useState('');
  const [scheduleForm, setScheduleForm] = useState(null);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [originalByDoctor, setOriginalByDoctor] = useState({});
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  const handleStep1Next = (data) => {
    setClinicId(data.clinicId);
    setError('');
    setCurrentStep(1);
  };

  const handleStep2Next = (data) => {
    setScheduleForm(data.scheduleForm);
    setError('');
    setCurrentStep(2);
  };

  const handleStep3Next = (data) => {
    setSelectedDoctorIds(data.selectedDoctorIds);
    setDoctors(data.doctors);
    setError('');
    setCurrentStep(3);
  };

  const handleStep4Next = (data) => {
    setOriginalByDoctor(data?.originalByDoctor || {});
    setError('');
    setCurrentStep(4);
  };

  const handleStep5Complete = (data) => {
    setSummary(data);
    setError('');
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setClinicId('');
    setScheduleForm(null);
    setSelectedDoctorIds([]);
    setDoctors([]);
    setOriginalByDoctor({});
    setSummary(null);
    setError('');
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    setError('');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Bulk Doctor Schedule Editor</h1>
          <p className="text-gray-600">
            Add the same schedule to multiple doctors. Select clinic, configure schedule, tick doctors, preview, then confirm.
          </p>
        </div>

        <ProgressSteps currentStep={currentStep} totalSteps={5} />

        {currentStep === 0 && (
          <Step1_SelectClinic
            onNext={handleStep1Next}
            error={error}
            setError={setError}
          />
        )}

        {currentStep === 1 && clinicId && (
          <Step2_AddSchedule
            clinicId={clinicId}
            onBack={() => goToStep(0)}
            onNext={handleStep2Next}
            error={error}
            setError={setError}
          />
        )}

        {currentStep === 2 && clinicId && scheduleForm && (
          <Step3_SelectDoctors
            clinicId={clinicId}
            onBack={() => goToStep(1)}
            onNext={handleStep3Next}
            error={error}
            setError={setError}
          />
        )}

        {currentStep === 3 && clinicId && scheduleForm && selectedDoctorIds.length > 0 && doctors.length > 0 && (
          <Step4_Preview
            clinicId={clinicId}
            scheduleForm={scheduleForm}
            selectedDoctorIds={selectedDoctorIds}
            doctors={doctors}
            onBack={() => goToStep(2)}
            onNext={handleStep4Next}
            error={error}
            setError={setError}
          />
        )}

        {currentStep === 4 && !summary && clinicId && scheduleForm && selectedDoctorIds.length > 0 && doctors.length > 0 && (
          <Step5_Confirm
            clinicId={clinicId}
            scheduleForm={scheduleForm}
            selectedDoctorIds={selectedDoctorIds}
            doctors={doctors}
            originalByDoctor={originalByDoctor}
            onBack={() => goToStep(3)}
            onComplete={handleStep5Complete}
            error={error}
            setError={setError}
          />
        )}

        {currentStep === 4 && summary && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete</h2>
              <p className="text-gray-600">
                {summary.isDeleteMode
                  ? `Schedule deleted from ${summary.successCount} doctor${summary.successCount !== 1 ? 's' : ''}.`
                  : `Schedule added to ${summary.successCount} doctor${summary.successCount !== 1 ? 's' : ''}.`}
                {summary.failedCount > 0 && ` ${summary.failedCount} failed.`}
              </p>
            </div>
            {summary.successCount > 0 && (
              <Link
                to={`/clinics/${clinicId}#doctor-schedules`}
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
              >
                View Clinic Doctor Schedules
              </Link>
            )}
            <div className="flex justify-end">
              <button
                onClick={handleRestart}
                className="px-8 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 font-medium"
              >
                Start New
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorScheduleEditor;
